import { FC, useMemo } from 'react';
import { format } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';
import { dateFormat } from 'constants/formats';
import {
  IncentiveOffer,
  IncentiveOfferStatus,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';

import GoBackButton from 'modules/_shared/components/Buttons/GoBackButton';
import Breadcrumb, {
  BreadcrumbItem,
} from 'modules/_shared/components/Breadcrumb';
import IncentiveOfferStatusBadge from '../../../components/IncentiveOfferStatusBadge';
import IncentiveOfferManageActions from '../../../components/IncentiveOfferManageActions';

type IncentiveOfferDetailHeaderProps = {
  offer: IncentiveOffer;
  offerReference: string;
  offerStatus: IncentiveOfferStatus;
  societyId: string;
  societyName: string;
  isAdmin: boolean;
  userBeneficiaryIds: string[];
  beneficiaryQueryParam: string;
};

const IncentiveOfferDetailHeader: FC<IncentiveOfferDetailHeaderProps> = ({
  offer,
  offerReference,
  offerStatus: status,
  societyId,
  societyName,
  isAdmin,
  userBeneficiaryIds,
  beneficiaryQueryParam,
}) => {
  const { t } = useTranslate();
  const location = useLocation();
  const beneficiaryQuery = new URLSearchParams(location.search).get('beneficiaryId');
  const pq = beneficiaryQuery ? `?beneficiaryId=${beneficiaryQuery}` : '';

  const isSellOffer = offer.type === 'SELL';

  const partnerName = useMemo(() => {
    if (offer.isSellerNameHidden && offer.type === 'SELL')
      return t('HiddenName');
    if (typeof offer.beneficiary === 'string') return '';
    return (offer.beneficiary as IncentivesMarketBeneficiary)?.name || '';
  }, [offer.beneficiary, offer.isSellerNameHidden, offer.type, t]);

  const offerTitle = isSellOffer ? t('SellOffer') : t('BuyOffer');

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('Dashboard'), to: '/' },
      { label: societyName, to: `/sociedad/${societyId}` },
      {
        label: t('IncentivesMarket'),
        to: `/mercado-de-incentivos/${societyId}${pq}`,
      },
      { label: t('Offers'), to: `/ofertas-incentivos/${societyId}${pq}` },
      { label: offerReference, isActive: true },
    ],
    [t, societyName, societyId, offerReference, pq]
  );

  return (
    <>
      <div className="nk-block-head-content mb-3">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="nk-block-head page-header-subtitle">
        <div className="nk-block-between">
          <div className="nk-block-head-content">
            <h3 className="nk-block-title page-title">
              {offerTitle}
              <span className="badge badge-outline badge-primary ml-2">
                {offerReference}
              </span>
              <IncentiveOfferStatusBadge status={status} />
            </h3>
            <div className="nk-block-des text-soft">
              <p>
                {t('PublishedBy')} {partnerName} {t('On').toLowerCase()}{' '}
                {format(new Date(offer.createdAt), dateFormat)}
              </p>
            </div>
          </div>

          <div className="nk-block-head-content">
            <div className="toggle-wrap nk-block-tools-toggle">
              <div className="toggle-expand-content" data-content="pageMenu">
                <ul className="nk-block-tools g-3">
                  <IncentiveOfferManageActions
                    offer={offer}
                    societyId={societyId}
                    isAdmin={isAdmin}
                    userBeneficiaryIds={userBeneficiaryIds}
                    beneficiaryQuerySuffix={beneficiaryQueryParam}
                  />
                  <li>
                    <GoBackButton />
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default IncentiveOfferDetailHeader;
